<template>
  <section class="image-with-cards-module-component">
    <div
      v-if="device !== 'desktop'"
      class="cards inner-container">
      <div class="card blue">
        <BlurBG
          theme="dark"
          :blob-size="1.2">
          <template #default>
            <div class="card-inner blue">
              <h2>{{ data.cards[0].card_title }}</h2>
              <div
                class="content"
                v-html="data.cards[0].card_description" />
              <ArrowCircleButton
                v-if="data.cards[0].button.title"
                :link="data.cards[0].button"
                color="white" />
            </div>
          </template>
        </BlurBG>
      </div>
    </div>
    <div class="background-image inner-container">
      <img
        ref="bgImg"
        class="parallax-bg"
        :src="data.bg_img.url"
        alt="" />
    </div>
    <div
      v-if="device !== 'desktop'"
      class="cards inner-container">
      <div class="card">
        <div class="card-inner bg-bicara-white">
          <h2>{{ data.cards[1].card_title }}</h2>
          <div
            class="content"
            v-html="data.cards[1].card_description" />
          <ArrowCircleButton
            v-if="data.cards[1].button.title"
            :link="data.cards[1].button"
            color="white" />
        </div>
      </div>
    </div>
    <div
      v-if="device === 'desktop'"
      class="cards inner-container">
      <div
        v-for="(card, index) of data.cards"
        :key="`parallax-card-${index}`"
        ref="cards"
        :class="index === 0 ? 'blue' : ''"
        class="card">
        <template v-if="index === 0">
          <BlurBG
            theme="dark"
            :blob-size="1.2">
            <template #default>
              <div
                class="card-inner"
                :class="index === 0 ? 'blue' : ''">
                <h2>{{ card.card_title }}</h2>
                <div
                  class="content"
                  v-html="card.card_description" />
                <ArrowCircleButton
                  v-if="card.button.title"
                  :link="card.button"
                  color="white" />
              </div>
            </template>
          </BlurBG>
        </template>
        <template v-else>
          <div class="card-inner bg-bicara-white">
            <h2>{{ card.card_title }}</h2>
            <div
              class="content"
              v-html="card.card_description" />
            <ArrowCircleButton
              v-if="card.button.title"
              :link="card.button"
              color="white" />
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "ImageWithCardsComponent",
  "CLASSNAME": "image-with-cards-module-component",
  "URL": "http://vuevitewp.local/"
 }
#ENDMETA */
import { ref, onMounted, onBeforeUnmount } from 'vue'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import BlurBG from '@/components/utility/BlurBG.vue'
import ArrowCircleButton from '@/components/utility/ArrowCircleButton.vue'
import useUtils from '@/composables/useUtils'

gsap.registerPlugin(ScrollTrigger)
const { genUUID, device } = useUtils()
defineProps(['data'])
const bgImg = ref(null)
const cards = ref(null)
const card1Id = genUUID()
const card2Id = genUUID()
const bgImgId = genUUID()

const parallaxBg = () => {
  let mm = gsap.matchMedia()
  mm.add('(min-width: 768px)', () => {
    gsap.fromTo(
      bgImg.value,
      {
        y: 50,
      },
      {
        y: 0,
        transformOrigin: 'center',
        scrollTrigger: {
          trigger: bgImg.value,
          id: bgImgId,
          start: 'top-=50% center',
          end: 'bottom+=50% center',
          scrub: true,
        },
      }
    )
  })
}

const parallaxCards = () => {
  let mm = gsap.matchMedia()
  mm.add('(min-width: 768px)', () => {
    if (!cards.value) return
    cards.value.forEach((card, index) => {
      if (index === 0) {
        gsap.fromTo(
          card,
          {
            y: 30,
          },
          {
            y: -200,
            scrollTrigger: {
              trigger: card,
              id: card1Id,
              start: 'top-=100% center',
              end: 'bottom+=50% center',
              scrub: true,
            },
          }
        )
      } else {
        gsap.fromTo(
          card,
          {
            y: -100,
          },
          {
            y: 50,
            scrollTrigger: {
              trigger: card,
              id: card2Id,
              start: 'top-=50% center',
              end: 'bottom+=50% center',
              scrub: true,
            },
          }
        )
      }
    })
  })
}

onMounted(() => {
  parallaxBg()
  parallaxCards()
})

onBeforeUnmount(() => {
  ScrollTrigger.getById(card1Id)?.kill()
  ScrollTrigger.getById(card2Id)?.kill()
  ScrollTrigger.getById(bgImgId)?.kill()
})
</script>

<style lang="scss">
.image-with-cards-module-component {
  @apply relative mb-0 pb-0 pt-0 lg:mb-[380px] lg:py-10;
  .inner-container.background-image {
    @apply relative z-1 min-h-[360px] overflow-hidden md:min-h-[580px];
    .parallax-bg {
      @apply absolute h-full w-full origin-center object-cover object-center;
      z-index: -1;
    }
  }
  .inner-container.cards {
    @apply relative bottom-10 left-0 right-0 z-2 grid grid-cols-1 overflow-visible px-md lg:absolute lg:bottom-[-250px] lg:grid-cols-2 lg:px-14;
    .card {
      @apply mt-0 h-fit lg:mt-28 lg:min-h-[380px];
      .card-inner {
        @apply rounded-[16px] px-[30px] py-[30px] lg:px-[50px] lg:py-[55px];
        h2 {
          @apply header-4 mb-6 text-bicara-blue;
        }
        .content {
          @apply body-lg text-bicara-charcoal;
          * {
            @apply body-md lg:body-lg;
          }
        }
      }
      &.blue {
        @apply relative bottom-[-80px] z-2 mt-0 md:bottom-[-160px];
        .card-inner {
          h2 {
            @apply header-4 mb-6 text-bicara-yellow;
          }
          .content {
            @apply mb-2 text-white lg:mb-20;
            * {
              @apply body-md lg:body-lg;
            }
          }
          a {
            @apply mb-0;
          }
        }
      }
    }
  }
}
</style>
